import type { OpportunitySet } from "../types/opportunities.types"

export const OPPORTUNITY_SET_ORDER_STORAGE_KEY = "nexus_opportunity_set_order"

export function readStoredOpportunitySetOrder(): string[] {
  try {
    const stored = localStorage.getItem(OPPORTUNITY_SET_ORDER_STORAGE_KEY)
    if (!stored) return []
    const parsed: unknown = JSON.parse(stored)
    if (!Array.isArray(parsed) || !parsed.every((id) => typeof id === "string")) return []
    return parsed
  } catch { /* corrupted or private browsing */ }
  return []
}

export function writeStoredOpportunitySetOrder(ids: readonly string[]): void {
  try {
    localStorage.setItem(OPPORTUNITY_SET_ORDER_STORAGE_KEY, JSON.stringify(ids))
  } catch { /* private browsing etc. */ }
}

function compareByCreatedAtDesc(a: OpportunitySet, b: OpportunitySet): number {
  return Date.parse(b.createdAt) - Date.parse(a.createdAt)
}

/**
 * Orders sets by stored drag order. Sets not yet in storage (e.g. a freshly
 * created generating card) are prepended on the left, newest first.
 */
export function orderOpportunitySets(
  sets: readonly OpportunitySet[],
  storedOrder: readonly string[],
): OpportunitySet[] {
  if (storedOrder.length === 0) return [...sets]

  const setMap = new Map(sets.map((s) => [s.id, s]))
  const ordered: OpportunitySet[] = []
  for (const id of storedOrder) {
    const s = setMap.get(id)
    if (s) {
      ordered.push(s)
      setMap.delete(id)
    }
  }

  const unknown = [...setMap.values()].sort(compareByCreatedAtDesc)
  return [...unknown, ...ordered]
}

/**
 * Prepends newly appeared set ids to persisted drag order and drops stale ids.
 */
export function mergeNewSetIdsIntoStoredOrder(
  sets: readonly OpportunitySet[],
  storedOrder: readonly string[],
): string[] {
  const currentIds = new Set(sets.map((s) => s.id))
  const pruned = storedOrder.filter((id) => currentIds.has(id))
  const known = new Set(pruned)
  const newIds = sets
    .filter((s) => !known.has(s.id))
    .sort(compareByCreatedAtDesc)
    .map((s) => s.id)

  if (newIds.length === 0 && pruned.length === storedOrder.length) {
    return [...storedOrder]
  }
  return [...newIds, ...pruned]
}

export function getSkeletonCount(fallback = 3, max = 8): number {
  try {
    const raw = localStorage.getItem(OPPORTUNITY_SET_ORDER_STORAGE_KEY)
    if (!raw) return fallback
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed) || parsed.length === 0) return fallback
    return Math.min(parsed.length, max)
  } catch {
    return fallback
  }
}
